import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Calendar,
  CreditCard, RefreshCw, Loader2, Crown, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const AdminRevenue = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      // Fetch payment data
      const paymentsRes = await axios.get('/membership/admin/all-payment-requests');
      const payments = paymentsRes.data?.requests || [];
      
      // Calculate stats from approved payments
      const approvedPayments = payments.filter(p => p.status === 'approved');
      const pendingPayments = payments.filter(p => p.status === 'pending');
      
      const totalRevenue = approvedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const monthlyRevenue = approvedPayments
        .filter(p => {
          const date = new Date(p.approved_at || p.created_at);
          const now = new Date();
          return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        })
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      
      setStats({
        totalRevenue,
        monthlyRevenue,
        totalTransactions: approvedPayments.length,
        pendingPayments: pendingPayments.length,
        pendingAmount: pendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0)
      });
      
      setTransactions(payments.slice(0, 20));
    } catch (error) {
      console.error('Revenue fetch error:', error);
      // Fallback stats
      setStats({
        totalRevenue: 0,
        monthlyRevenue: 0,
        totalTransactions: 0,
        pendingPayments: 0,
        pendingAmount: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Onaylandı</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">Bekliyor</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Reddedildi</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary" />
            Gelir Yönetimi
          </h1>
          <p className="text-slate-400 text-sm">Platform gelirlerini ve ödemeleri takip edin</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRevenueData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Toplam Gelir</p>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(stats?.totalRevenue)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Bu Ay</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(stats?.monthlyRevenue)}</p>
              </div>
              <Calendar className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Onaylanan İşlem</p>
                <p className="text-2xl font-bold text-blue-400">{stats?.totalTransactions || 0}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-yellow-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Bekleyen</p>
                <p className="text-2xl font-bold text-yellow-400">{stats?.pendingPayments || 0}</p>
                {stats?.pendingAmount > 0 && (
                  <p className="text-xs text-yellow-400/70">{formatCurrency(stats.pendingAmount)}</p>
                )}
              </div>
              <Loader2 className="h-8 w-8 text-yellow-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart Placeholder */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Gelir Dağılımı
            </CardTitle>
            <CardDescription>Aylık gelir özeti</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 text-primary" />
                  <span>Pro Üyelikler</span>
                </div>
                <span className="font-bold text-primary">{formatCurrency(stats?.totalRevenue)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-blue-400" />
                  <span>Marketplace Komisyonları</span>
                </div>
                <span className="font-bold text-slate-400">{formatCurrency(0)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-amber-400" />
                  <span>Partner Gelirleri</span>
                </div>
                <span className="font-bold text-slate-400">{formatCurrency(0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Hızlı İstatistikler</CardTitle>
            <CardDescription>Platform performans metrikleri</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Ortalama İşlem</span>
                <span className="font-bold">
                  {stats?.totalTransactions > 0 
                    ? formatCurrency(stats.totalRevenue / stats.totalTransactions)
                    : formatCurrency(0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Dönüşüm Oranı</span>
                <span className="font-bold text-primary">-</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Churn Oranı</span>
                <span className="font-bold text-green-400">-</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">MRR (Aylık Tekrarlayan)</span>
                <span className="font-bold text-primary">{formatCurrency(stats?.monthlyRevenue)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Son İşlemler</CardTitle>
          <CardDescription>En son ödeme işlemleri</CardDescription>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Henüz işlem yok</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div 
                  key={tx.id} 
                  className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-700/50"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{tx.user_email}</p>
                      <p className="text-xs text-slate-500">
                        {tx.plan_type === 'pro' ? 'Pro Üyelik' : tx.plan_type} • {formatDate(tx.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(tx.status)}
                    <span className={`font-bold ${tx.status === 'approved' ? 'text-green-400' : 'text-slate-400'}`}>
                      {formatCurrency(tx.amount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRevenue;
